import { useState, type FormEvent } from "react";

const SUBMIT_ADDR = "https://readdy.ai/api/form/db8u37gkk00l6eltrmq0";

type Status = "idle" | "submitting" | "success" | "error";

export default function FeedbackForm() {
  const [status, setStatus] = useState<Status>("idle");
  const [feedbackMsg, setFeedbackMsg] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formEl = event.currentTarget;
    const formData = new FormData(formEl);

    const honeypot = String(formData.get("website_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      setFeedbackMsg("感谢您的反馈，我们会认真阅读并处理。");
      formEl.reset();
      return;
    }

    setStatus("submitting");
    setFeedbackMsg("");

    const params = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      const str = String(value).trim();
      if (str) params.append(key, str);
    });

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = responseText ? JSON.parse(responseText) : null;
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message ||
        parsed?.message ||
        parsed?.meta?.detail ||
        responseText ||
        "";
      const isSpam = String(serverMsg).toLowerCase().includes("spam");
      const ok = response.ok && parsed?.code === "OK";

      if (ok) {
        setStatus("success");
        setFeedbackMsg("感谢您的反馈，我们会认真阅读并处理。");
        formEl.reset();
      } else if (isSpam) {
        setStatus("error");
        setFeedbackMsg(serverMsg || "提交未通过校验，请检查后重试。");
      } else {
        setStatus("error");
        setFeedbackMsg(serverMsg || "提交失败，请稍后重试。");
      }
    } catch {
      setStatus("error");
      setFeedbackMsg("网络异常，提交失败，请稍后重试。");
    }
  };

  return (
    <form
      data-readdy-form
      onSubmit={handleSubmit}
      className="w-full rounded-lg bg-background-50 border border-background-200 p-6 md:p-8"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <label className="block text-sm font-medium text-foreground-800 mb-2">
            您的称呼
          </label>
          <input
            type="text"
            name="name"
            required
            placeholder="请输入您的称呼"
            className="w-full h-11 px-4 rounded-md bg-background-100 text-sm text-foreground-900 outline-none focus:ring-2 focus:ring-primary-400 placeholder:text-foreground-400"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground-800 mb-2">
            联系邮箱
          </label>
          <input
            type="email"
            name="email"
            required
            placeholder="name@example.com"
            className="w-full h-11 px-4 rounded-md bg-background-100 text-sm text-foreground-900 outline-none focus:ring-2 focus:ring-primary-400 placeholder:text-foreground-400"
          />
        </div>
      </div>

      <div className="mt-5">
        <label className="block text-sm font-medium text-foreground-800 mb-2">
          反馈类型
        </label>
        <select
          name="topic"
          defaultValue="内容建议"
          className="w-full h-11 px-4 rounded-md bg-background-100 text-sm text-foreground-900 outline-none focus:ring-2 focus:ring-primary-400 cursor-pointer"
        >
          <option value="内容建议">内容建议</option>
          <option value="资源报错">资源报错</option>
          <option value="功能反馈">功能反馈</option>
          <option value="商务合作">商务合作</option>
          <option value="其他">其他</option>
        </select>
      </div>

      <div className="mt-5">
        <label className="block text-sm font-medium text-foreground-800 mb-2">
          建议内容
        </label>
        <textarea
          name="message"
          required
          maxLength={500}
          rows={5}
          placeholder="请描述您的建议或遇到的问题（最多500字）"
          className="w-full px-4 py-3 rounded-md bg-background-100 text-sm text-foreground-900 outline-none focus:ring-2 focus:ring-primary-400 placeholder:text-foreground-400 resize-none"
        ></textarea>
      </div>

      <input
        type="text"
        name="website_alt"
        className="contact-note-field"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
      />

      <div className="mt-6 flex flex-col sm:flex-row sm:items-center gap-4">
        <button
          type="submit"
          disabled={status === "submitting"}
          className="inline-flex items-center justify-center gap-2 h-11 px-7 rounded-md bg-primary-600 text-background-50 text-sm font-semibold hover:bg-primary-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors whitespace-nowrap cursor-pointer"
        >
          {status === "submitting" ? "提交中…" : "提交建议"}
        </button>

        {status === "success" && (
          <p className="text-sm text-primary-700 inline-flex items-center gap-1.5">
            <i className="ri-checkbox-circle-line"></i>
            {feedbackMsg}
          </p>
        )}
        {status === "error" && (
          <p className="text-sm text-accent-700 inline-flex items-center gap-1.5">
            <i className="ri-error-warning-line"></i>
            {feedbackMsg}
          </p>
        )}
      </div>
    </form>
  );
}